<template>
  <ehc-page v-if="userHasPermission(['superAdmin'])">
    <v-card class="pa-3 mb-3">
      <ehc-header text="Admin Settings"></ehc-header>
      <v-expansion-panels v-model="settingsPanel" accordion class="mt-2">
        <v-expansion-panel>
          <v-expansion-panel-header class="text-subtitle-1 font-weight-medium">
            Global App Settings
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <ehc-form
              dense
              color="primary"
              :meta="globalAppSettingsMeta"
              v-model="editSettings"
              @save="saveGlobalAppSettings()"
            >
            </ehc-form>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>
            <div
              class="d-flex align-center justify-space-between flex-grow-1 pr-2"
            >
              <span class="text-subtitle-1 font-weight-medium"
                >How-To Videos</span
              >
              <v-btn
                small
                color="primary"
                class="ml-2"
                @click.stop="openHowToVideoDialog()"
              >
                <v-icon left small>mdi-plus</v-icon>
                Add video
              </v-btn>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p class="mb-3">
              Training videos shown on the How-To Videos page. Optional
              in-product slots still drive setup, guidebook links, and welcome
              emails.
            </p>
            <v-alert
              v-if="howToVideosError"
              type="error"
              dense
              outlined
              class="mb-3"
            >
              {{ howToVideosError }}
            </v-alert>
            <v-alert
              v-if="howToVideosSuccess"
              type="success"
              dense
              outlined
              class="mb-3"
            >
              {{ howToVideosSuccess }}
            </v-alert>
            <v-simple-table dense>
              <template #default>
                <thead>
                  <tr>
                    <th class="text-left">Title</th>
                    <th class="text-left">Description</th>
                    <th class="text-left">Tags</th>
                    <th class="text-left">URL</th>
                    <th class="text-left">In-product use</th>
                    <th class="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(video, index) in howToVideos"
                    :key="video.id || index"
                  >
                    <td>{{ video.title || "—" }}</td>
                    <td class="text-caption">
                      {{ truncateText(video.description, 80) }}
                    </td>
                    <td>
                      <v-chip
                        v-for="tag in video.tags"
                        :key="tag"
                        x-small
                        outlined
                        class="mr-1 mb-1"
                      >
                        {{ tag }}
                      </v-chip>
                      <span v-if="!(video.tags && video.tags.length)" class="grey--text">
                        —
                      </span>
                    </td>
                    <td class="text-caption">
                      {{ truncateText(video.url, 40) }}
                    </td>
                    <td>{{ howToPlacementLabel(video.placement) }}</td>
                    <td class="text-right text-no-wrap">
                      <v-btn
                        icon
                        small
                        :disabled="index === 0"
                        @click="moveHowToVideo(index, -1)"
                      >
                        <v-icon small>mdi-arrow-up</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        :disabled="index === howToVideos.length - 1"
                        @click="moveHowToVideo(index, 1)"
                      >
                        <v-icon small>mdi-arrow-down</v-icon>
                      </v-btn>
                      <v-btn
                        small
                        text
                        color="primary"
                        @click="openHowToVideoDialog(index)"
                      >
                        Edit
                      </v-btn>
                      <v-btn
                        small
                        text
                        color="error"
                        @click="removeHowToVideo(index)"
                      >
                        Remove
                      </v-btn>
                    </td>
                  </tr>
                  <tr v-if="!howToVideos.length">
                    <td colspan="6" class="grey--text text-center py-4">
                      No how-to videos yet. Add one, or leave unset to use the
                      built-in training videos until you save a list here.
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div class="d-flex justify-end mt-3">
              <ehc-btn
                color="primary"
                dark
                :loading="savingHowToVideos"
                @click="saveHowToVideos"
              >
                Save videos
              </ehc-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>
            <div
              class="d-flex align-center justify-space-between flex-grow-1 pr-2"
            >
              <span class="text-subtitle-1 font-weight-medium"
                >Property page alerts</span
              >
              <v-btn
                small
                color="primary"
                class="ml-2"
                @click.stop="openAlertDialog()"
              >
                <v-icon left small>mdi-plus</v-icon>
                Add alert
              </v-btn>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p class="mb-3">
              Announcement banners on the organization Properties page. Each
              alert supports a date range, max view count, and per-user dismiss.
            </p>
            <v-alert v-if="alertsError" type="error" dense outlined class="mb-3">
              {{ alertsError }}
            </v-alert>
            <v-alert
              v-if="alertsSuccess"
              type="success"
              dense
              outlined
              class="mb-3"
            >
              {{ alertsSuccess }}
            </v-alert>
            <v-simple-table dense>
              <template #default>
                <thead>
                  <tr>
                    <th class="text-left">ID</th>
                    <th class="text-left">Title</th>
                    <th class="text-left">Dates</th>
                    <th class="text-left">Max views</th>
                    <th class="text-left">Enabled</th>
                    <th class="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(alert, index) in propertyPageAlerts"
                    :key="alert.id || index"
                  >
                    <td>
                      <code>{{ alert.id }}</code>
                    </td>
                    <td>{{ alert.title || "—" }}</td>
                    <td class="text-caption">
                      {{ formatAlertDates(alert) }}
                    </td>
                    <td>
                      {{ alert.maxViews > 0 ? alert.maxViews : "Unlimited" }}
                    </td>
                    <td>
                      <v-chip
                        x-small
                        :color="alert.enabled ? 'success' : 'grey'"
                        text-color="white"
                      >
                        {{ alert.enabled ? "On" : "Off" }}
                      </v-chip>
                    </td>
                    <td class="text-right">
                      <v-btn
                        small
                        text
                        color="primary"
                        @click="openAlertDialog(index)"
                      >
                        Edit
                      </v-btn>
                      <v-btn
                        small
                        text
                        color="error"
                        @click="removeAlert(index)"
                      >
                        Remove
                      </v-btn>
                    </td>
                  </tr>
                  <tr v-if="!propertyPageAlerts.length">
                    <td colspan="6" class="grey--text text-center py-4">
                      No alerts configured. Add one, or leave unset to use the
                      built-in printable-guides fallback until you save a list
                      here.
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div class="d-flex justify-end mt-3">
              <ehc-btn
                color="primary"
                dark
                :loading="savingAlerts"
                @click="savePropertyPageAlerts"
              >
                Save alerts
              </ehc-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>
            <div
              class="d-flex align-center justify-space-between flex-grow-1 pr-2"
            >
              <span class="text-subtitle-1 font-weight-medium"
                >Special codes</span
              >
              <div class="d-flex flex-wrap justify-end" @click.stop>
                <v-btn
                  small
                  color="primary"
                  class="ml-2 mb-1"
                  @click="openSpecialCodeDialog()"
                >
                  <v-icon left small>mdi-plus</v-icon>
                  Add code
                </v-btn>
                <v-btn
                  small
                  color="secondary"
                  class="ml-2 mb-1"
                  @click="openSpecialCodeDialog(null, true)"
                >
                  <v-icon left small>mdi-star</v-icon>
                  Add Pro-10 template
                </v-btn>
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p class="mb-3">
              Redeemable codes on the Subscribe page that unlock a hidden Stripe
              option. Stored in
              <code>settings/specialCodes</code>. Codes are validated
              server-side.
            </p>
            <v-alert
              v-if="specialCodesError"
              type="error"
              dense
              outlined
              class="mb-3"
            >
              {{ specialCodesError }}
            </v-alert>
            <v-alert
              v-if="specialCodesSuccess"
              type="success"
              dense
              outlined
              class="mb-3"
            >
              {{ specialCodesSuccess }}
            </v-alert>
            <v-simple-table dense>
              <template #default>
                <thead>
                  <tr>
                    <th class="text-left">Code</th>
                    <th class="text-left">Offer</th>
                    <th class="text-left">Type</th>
                    <th class="text-left">Days</th>
                    <th class="text-left">Enabled</th>
                    <th class="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(entry, index) in specialCodes"
                    :key="entry.code || index"
                  >
                    <td>
                      <code>{{ entry.code || "—" }}</code>
                    </td>
                    <td>{{ entry.name || "—" }}</td>
                    <td>
                      {{ entry.subscriptionType || "—" }} /
                      {{ entry.subscriptionCount || "—" }}
                    </td>
                    <td>{{ entry.durationDays || "—" }}</td>
                    <td>
                      <v-chip
                        x-small
                        :color="entry.enabled ? 'success' : 'grey'"
                        text-color="white"
                      >
                        {{ entry.enabled ? "On" : "Off" }}
                      </v-chip>
                    </td>
                    <td class="text-right">
                      <v-btn
                        small
                        text
                        color="primary"
                        @click="openSpecialCodeDialog(index)"
                      >
                        Edit
                      </v-btn>
                      <v-btn
                        small
                        text
                        color="error"
                        @click="removeSpecialCode(index)"
                      >
                        Remove
                      </v-btn>
                    </td>
                  </tr>
                  <tr v-if="!specialCodes.length">
                    <td colspan="6" class="grey--text text-center py-4">
                      No special codes yet. Add one, or use “Add Pro-10
                      template” to prefill the 90-day Pro-10 offer.
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div class="d-flex justify-end mt-3">
              <ehc-btn
                color="primary"
                dark
                :loading="savingSpecialCodes"
                @click="saveSpecialCodes"
              >
                Save special codes
              </ehc-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header class="text-subtitle-1 font-weight-medium">
            Property type templates
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p class="mb-3">
              Managed on the
              <router-link to="/templates">Templates</router-link>
              page. Creating or deleting templates there updates these mappings.
            </p>
            <v-simple-table dense>
              <template #default>
                <thead>
                  <tr>
                    <th class="text-left">Property type</th>
                    <th class="text-left">Template ID</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in propertyTypeTemplateRows"
                    :key="row.typeKey"
                  >
                    <td>{{ row.typeKey }}</td>
                    <td>
                      <code>{{ row.templateId }}</code>
                    </td>
                  </tr>
                  <tr v-if="!propertyTypeTemplateRows.length">
                    <td colspan="2" class="grey--text text-center py-4">
                      No template mappings yet.
                      <router-link to="/templates">Add templates</router-link>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header class="text-subtitle-1 font-weight-medium">
            Default Property Template
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p
              @click="goToDefault"
              class="pointer text-h6"
              v-html="defaultLink"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header class="text-subtitle-1 font-weight-medium">
            Un-delete a property
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <ehc-form
              dense
              color="primary"
              :meta="undeletePropertyMeta"
              v-model="formData"
              @undelete="undeleteProperty()"
            >
            </ehc-form>
            {{ deleteMessage }}
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header class="text-subtitle-1 font-weight-medium">
            Email Templates
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <ehc-btn to="/admin/email-template" class="white--text mr-2">
              Edit Email Templates
            </ehc-btn>
            <ehc-btn to="/admin/reactivation-promo" class="white--text">
              Send Reactivation Promo
            </ehc-btn>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>

    <v-dialog v-model="alertDialog" max-width="560" persistent>
      <v-card class="pa-4">
        <v-card-title class="px-0 pt-0">
          {{ alertEditIndex === null ? "Add alert" : "Edit alert" }}
        </v-card-title>
        <v-card-text class="px-0">
          <v-text-field
            v-model="alertForm.id"
            label="ID"
            hint="Stable key for dismiss/view tracking (letters, numbers, _ -)"
            persistent-hint
            outlined
            dense
            class="mb-3"
            :disabled="alertEditIndex !== null"
          ></v-text-field>
          <v-text-field
            v-model="alertForm.title"
            label="Title"
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-textarea
            v-model="alertForm.body"
            label="Body"
            outlined
            dense
            rows="3"
            class="mb-3"
          ></v-textarea>
          <v-text-field
            v-model="alertForm.icon"
            label="Icon (MDI)"
            hint="e.g. mdi-printer-outline"
            persistent-hint
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-select
            v-model="alertForm.type"
            :items="alertTypeOptions"
            item-text="label"
            item-value="value"
            label="Type"
            outlined
            dense
            class="mb-3"
          ></v-select>
          <v-text-field
            v-model="alertForm.startAt"
            label="Start date"
            type="date"
            hint="Inclusive. Leave blank for already started."
            persistent-hint
            outlined
            dense
            class="mb-3"
            clearable
          ></v-text-field>
          <v-text-field
            v-model="alertForm.endAt"
            label="End date"
            type="date"
            hint="Inclusive last day shown. Leave blank for no end."
            persistent-hint
            outlined
            dense
            class="mb-3"
            clearable
          ></v-text-field>
          <v-text-field
            v-model.number="alertForm.maxViews"
            label="Max views"
            type="number"
            min="0"
            hint="0 = unlimited until dismissed or end date"
            persistent-hint
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-switch
            v-model="alertForm.enabled"
            label="Enabled"
            dense
            class="mt-0"
          ></v-switch>
          <v-alert v-if="alertDialogError" type="error" dense outlined class="mt-2 mb-0">
            {{ alertDialogError }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="px-0 pb-0">
          <v-spacer></v-spacer>
          <v-btn text :disabled="savingAlerts" @click="closeAlertDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="savingAlerts"
            @click="applyAlertDialog"
          >
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="howToVideoDialog" max-width="560" persistent>
      <v-card class="pa-4">
        <v-card-title class="px-0 pt-0">
          {{ howToVideoEditIndex === null ? "Add how-to video" : "Edit how-to video" }}
        </v-card-title>
        <v-card-text class="px-0">
          <v-text-field
            v-model="howToVideoForm.title"
            label="Title"
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-textarea
            v-model="howToVideoForm.description"
            label="Description"
            outlined
            dense
            rows="3"
            class="mb-3"
          ></v-textarea>
          <v-text-field
            v-model="howToVideoForm.url"
            label="YouTube URL"
            hint="Paste a watch, youtu.be, or embed URL"
            persistent-hint
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-select
            v-model="howToVideoForm.placement"
            :items="howToPlacementOptions"
            item-text="label"
            item-value="value"
            label="In-product use"
            hint="Optional. At most one video per slot."
            persistent-hint
            outlined
            dense
            class="mb-3"
          ></v-select>
          <v-combobox
            v-model="howToVideoForm.tags"
            :items="howToTagSuggestions"
            label="Tags"
            hint="Type a tag and press Enter. Used to filter videos on the How-To page."
            persistent-hint
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            class="mb-3"
          ></v-combobox>
          <v-alert
            v-if="howToVideoDialogError"
            type="error"
            dense
            outlined
            class="mt-2 mb-0"
          >
            {{ howToVideoDialogError }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="px-0 pb-0">
          <v-spacer></v-spacer>
          <v-btn text :disabled="savingHowToVideos" @click="closeHowToVideoDialog">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="savingHowToVideos"
            @click="applyHowToVideoDialog"
          >
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="specialCodeDialog" max-width="560" persistent>
      <v-card class="pa-4">
        <v-card-title class="px-0 pt-0">
          {{ specialCodeEditIndex === null ? "Add special code" : "Edit special code" }}
        </v-card-title>
        <v-card-text class="px-0">
          <v-text-field
            v-model="specialCodeForm.code"
            label="Code"
            hint="What the customer enters on Subscribe"
            persistent-hint
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="specialCodeForm.name"
            label="Offer name (button label)"
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="specialCodeForm.priceId"
            label="Stripe price ID"
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="specialCodeForm.productId"
            label="Stripe product ID"
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="specialCodeForm.itemId"
            label="Item ID"
            hint="Stored on the org as account.itemId"
            persistent-hint
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="specialCodeForm.subscriptionType"
            label="Subscription type"
            hint='e.g. "Pro-10"'
            persistent-hint
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model.number="specialCodeForm.subscriptionCount"
            label="Guidebook count"
            type="number"
            min="1"
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model.number="specialCodeForm.durationDays"
            label="Duration (days)"
            type="number"
            min="1"
            hint="Expires this many days after Stripe confirms payment"
            persistent-hint
            outlined
            dense
            class="mb-3"
          ></v-text-field>
          <v-select
            v-model="specialCodeForm.checkoutMode"
            :items="checkoutModeOptions"
            item-text="text"
            item-value="value"
            label="Checkout mode"
            outlined
            dense
            class="mb-3"
          ></v-select>
          <v-textarea
            v-model="specialCodeForm.note"
            label="Internal note"
            outlined
            dense
            rows="2"
            class="mb-3"
          ></v-textarea>
          <v-switch
            v-model="specialCodeForm.enabled"
            label="Enabled"
            dense
            class="mt-0"
          ></v-switch>
          <v-alert
            v-if="specialCodeDialogError"
            type="error"
            dense
            outlined
            class="mt-2 mb-0"
          >
            {{ specialCodeDialogError }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="px-0 pb-0">
          <v-spacer></v-spacer>
          <v-btn text :disabled="savingSpecialCodes" @click="closeSpecialCodeDialog">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="savingSpecialCodes"
            @click="applySpecialCodeDialog"
          >
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ehc-message
      v-model="message"
      @cancel="message = closeMessage"
      @confirmEditDefaultTemplate="confirmEditDefaultTemplateProceed"
    ></ehc-message>
  </ehc-page>
</template>

<script>
import apiMixin from "@/mixins/api.vue";
import PropertyActions from "@/mixins/propertyActions.vue";
import ehcButtonSmall from "../../components/global/ehc-button-small.vue";
import {
  DEFAULT_PROPERTY_PAGE_ALERTS,
  PROPERTY_PAGE_ALERT_TYPES,
  emptyPropertyPageAlert,
  normalizePropertyPageAlert,
  validatePropertyPageAlerts,
} from "@/utils/propertyPageAlerts.js";
import {
  HOW_TO_VIDEO_PLACEMENT_OPTIONS,
  emptyHowToVideo,
  newHowToVideoId,
  normalizeHowToVideo,
  placementLabel,
  resolveHowToVideos,
  trainingUrlFieldsFromHowToVideos,
  validateHowToVideos,
} from "@/utils/howToVideos.js";
import {
  CHECKOUT_MODES,
  SPECIAL_CODES_COLLECTION,
  SPECIAL_CODES_DOC_ID,
  defaultPro10SpecialCode,
  emptySpecialCode,
  normalizeSpecialCode,
  validateSpecialCodes,
} from "@/utils/specialCodes.js";
import firebase from "firebase";

export default {
  components: { ehcButtonSmall },
  mixins: [apiMixin, PropertyActions],
  data() {
    return {
      message: { show: false },
      closeMessage: { show: false },
      edit: false,
      settingsPanel: null,
      //just add to this to get more settings
      globalAppSettingsMeta: [
        {
          type: "number",
          label: "default property limit",
          key: "defaultPropertyLimit",
        },
        {
          type: "number",
          label: "premium property limit",
          key: "premiumPropertyLimit",
        },
        {
          type: "textArea",
          label: "subscribe message",
          key: "subscribeMessage",
        },
        {
          type: "switch",
          label: "Enable Nearby Pages",
          key: "enableNearbyPages",
          float: "left",
        },
        {
          type: "switch",
          label: "Show Nearby Clear Buttons (admin)",
          key: "showNearbyClearButtons",
          float: "left",
        },
        { type: "space", height: "40px", class: "my-4" },
        {
          type: "switch",
          label: "Send Guest Notifications to Owner",
          key: "sendGuestNotificationsToOwner",
          float: "left",
        },
        {
          type: "switch",
          label: "Send Guest Notification to Support",
          key: "sendGuestNotificationToSupport",
          float: "left",
        },
        { type: "space", height: "40px", class: "my-4" },
        {
          type: "text",
          label: "Subscription Deadline Date",
          key: "subscriptionsDeadline",
          appendOuter:
            "The date after which new properties cannot be accessed without a subscription (as a simple string).",
        },
        { type: "button", label: "Save", key: "save", emitOnClick: "save" },
      ],
      editSettings: {},
      howToVideos: [],
      howToPlacementOptions: HOW_TO_VIDEO_PLACEMENT_OPTIONS,
      howToVideoDialog: false,
      howToVideoEditIndex: null,
      howToVideoForm: emptyHowToVideo(),
      howToVideoDialogError: null,
      howToVideosError: null,
      howToVideosSuccess: null,
      savingHowToVideos: false,
      howToVideosDirty: false,
      propertyPageAlerts: [],
      alertTypeOptions: PROPERTY_PAGE_ALERT_TYPES,
      alertDialog: false,
      alertEditIndex: null,
      alertForm: emptyPropertyPageAlert(),
      alertDialogError: null,
      alertsError: null,
      alertsSuccess: null,
      savingAlerts: false,
      propertyPageAlertsDirty: false,
      specialCodes: [],
      checkoutModeOptions: CHECKOUT_MODES,
      specialCodeDialog: false,
      specialCodeEditIndex: null,
      specialCodeForm: emptySpecialCode(),
      specialCodeDialogError: null,
      specialCodesError: null,
      specialCodesSuccess: null,
      savingSpecialCodes: false,
      formData: {
        propertyId: "",
      },
      undeletePropertyMeta: [
        { type: "text", label: "Property ID", key: "propertyId" },
        {
          type: "button",
          label: "Undelete",
          key: "undelete",
          emitOnClick: "undelete",
        },
      ],
      deleteMessage: "",
    };
  },
  watch: {
    gcvAppSettings() {
      // Don't clobber in-progress alert or video edits when settings refresh.
      if (
        this.propertyPageAlertsDirty ||
        this.alertDialog ||
        this.howToVideosDirty ||
        this.howToVideoDialog
      ) {
        this.editSettings = {
          ...(this.$store.getters.appSettings || {}),
        };
        return;
      }
      this.loadEditSettings();
    },
  },
  mounted() {
    this.loadEditSettings();
    this.loadSpecialCodes();
  },
  created() {
    this.loadEditSettings();
  },
  computed: {
    defaultLink() {
      return `https://everhost.io/login/${this.$store.getters.defaultPropertyId}`;
    },
    propertyTypeTemplateRows() {
      const map =
        this.$store.getters.appSettings?.propertyTypeTemplates ||
        this.editSettings?.propertyTypeTemplates ||
        {};
      return Object.entries(map)
        .map(([typeKey, templateId]) => ({
          typeKey,
          templateId: String(templateId || "").trim(),
        }))
        .filter((row) => row.templateId)
        .sort((a, b) => {
          if (a.typeKey === "default") return -1;
          if (b.typeKey === "default") return 1;
          return a.typeKey.localeCompare(b.typeKey);
        });
    },
    howToTagSuggestions() {
      const seen = new Set();
      const tags = [];
      this.howToVideos.forEach((video) => {
        (video.tags || []).forEach((tag) => {
          const key = String(tag || "").toLowerCase();
          if (!key || seen.has(key)) {
            return;
          }
          seen.add(key);
          tags.push(tag);
        });
      });
      return tags.sort((a, b) => a.localeCompare(b));
    },
  },
  methods: {
    loadEditSettings() {
      const settings = {
        ...(this.$store.getters.appSettings || {}),
      };
      this.editSettings = settings;
      this.howToVideos = resolveHowToVideos(settings);
      this.howToVideosDirty = false;
      if (Object.prototype.hasOwnProperty.call(settings, "propertyPageAlerts")) {
        const list = Array.isArray(settings.propertyPageAlerts)
          ? settings.propertyPageAlerts
          : [];
        this.propertyPageAlerts = list.map(normalizePropertyPageAlert);
      } else {
        // Preview the fallback until an admin saves an explicit list.
        this.propertyPageAlerts = DEFAULT_PROPERTY_PAGE_ALERTS.map(
          normalizePropertyPageAlert,
        );
      }
      this.propertyPageAlertsDirty = false;
    },
    truncateText(value, max = 80) {
      const text = String(value || "").trim();
      if (text.length <= max) {
        return text || "—";
      }
      return `${text.slice(0, max).trim()}…`;
    },
    howToPlacementLabel(placement) {
      return placementLabel(placement);
    },
    openHowToVideoDialog(index = null) {
      this.howToVideoDialogError = null;
      this.howToVideosError = null;
      this.howToVideosSuccess = null;
      if (index === null || index === undefined) {
        this.howToVideoEditIndex = null;
        this.howToVideoForm = emptyHowToVideo();
      } else {
        this.howToVideoEditIndex = index;
        const next = normalizeHowToVideo(this.howToVideos[index]);
        this.howToVideoForm = {
          ...next,
          tags: [...next.tags],
        };
      }
      this.howToVideoDialog = true;
    },
    closeHowToVideoDialog() {
      this.howToVideoDialog = false;
      this.howToVideoDialogError = null;
      this.howToVideoEditIndex = null;
      this.howToVideoForm = emptyHowToVideo();
    },
    async applyHowToVideoDialog() {
      const next = normalizeHowToVideo(this.howToVideoForm);
      if (!next.id) {
        next.id = newHowToVideoId();
      }
      const draft = [...this.howToVideos];
      if (this.howToVideoEditIndex === null) {
        draft.push(next);
      } else {
        draft.splice(this.howToVideoEditIndex, 1, next);
      }
      const error = validateHowToVideos(draft);
      if (error) {
        this.howToVideoDialogError = error;
        return;
      }
      this.howToVideos = draft;
      this.howToVideosDirty = true;
      this.closeHowToVideoDialog();
      await this.saveHowToVideos();
    },
    async removeHowToVideo(index) {
      this.howToVideos = this.howToVideos.filter((_, i) => i !== index);
      this.howToVideosDirty = true;
      this.howToVideosSuccess = null;
      await this.saveHowToVideos();
    },
    moveHowToVideo(index, delta) {
      const nextIndex = index + delta;
      if (nextIndex < 0 || nextIndex >= this.howToVideos.length) {
        return;
      }
      const draft = [...this.howToVideos];
      const [item] = draft.splice(index, 1);
      draft.splice(nextIndex, 0, item);
      this.howToVideos = draft;
      this.howToVideosDirty = true;
      this.howToVideosSuccess = null;
    },
    async saveHowToVideos() {
      this.howToVideosError = null;
      this.howToVideosSuccess = null;
      const normalized = this.howToVideos.map(normalizeHowToVideo);
      const error = validateHowToVideos(normalized);
      if (error) {
        this.howToVideosError = error;
        return;
      }
      this.savingHowToVideos = true;
      try {
        await this.apiUpdateAppSettings({
          howToVideos: normalized,
          ...trainingUrlFieldsFromHowToVideos(normalized),
        });
        this.howToVideos = normalized;
        this.howToVideosDirty = false;
        this.howToVideosSuccess = `Saved ${normalized.length} how-to video(s).`;
        this.$store.commit("setAlert", {
          type: "success",
          text: this.howToVideosSuccess,
        });
      } catch (err) {
        this.howToVideosError =
          err?.message || "Failed to save how-to videos.";
        this.$store.commit("setAlert", {
          type: "error",
          text: this.howToVideosError,
        });
      } finally {
        this.savingHowToVideos = false;
      }
    },
    formatAlertDates(alert) {
      const start = alert.startAt || "…";
      const end = alert.endAt || "…";
      return `${start} → ${end}`;
    },
    openAlertDialog(index = null) {
      this.alertDialogError = null;
      this.alertsError = null;
      this.alertsSuccess = null;
      if (index === null || index === undefined) {
        this.alertEditIndex = null;
        this.alertForm = emptyPropertyPageAlert();
      } else {
        this.alertEditIndex = index;
        this.alertForm = {
          ...normalizePropertyPageAlert(this.propertyPageAlerts[index]),
        };
      }
      this.alertDialog = true;
    },
    closeAlertDialog() {
      this.alertDialog = false;
      this.alertDialogError = null;
      this.alertEditIndex = null;
      this.alertForm = emptyPropertyPageAlert();
    },
    async applyAlertDialog() {
      const next = normalizePropertyPageAlert(this.alertForm);
      const draft = [...this.propertyPageAlerts];
      if (this.alertEditIndex === null) {
        draft.push(next);
      } else {
        draft.splice(this.alertEditIndex, 1, next);
      }
      const error = validatePropertyPageAlerts(draft);
      if (error) {
        this.alertDialogError = error;
        return;
      }
      this.propertyPageAlerts = draft;
      this.propertyPageAlertsDirty = true;
      this.closeAlertDialog();
      await this.savePropertyPageAlerts();
    },
    async removeAlert(index) {
      this.propertyPageAlerts = this.propertyPageAlerts.filter(
        (_, i) => i !== index,
      );
      this.propertyPageAlertsDirty = true;
      this.alertsSuccess = null;
      await this.savePropertyPageAlerts();
    },
    async savePropertyPageAlerts() {
      this.alertsError = null;
      this.alertsSuccess = null;
      const normalized = this.propertyPageAlerts.map(normalizePropertyPageAlert);
      const error = validatePropertyPageAlerts(normalized);
      if (error) {
        this.alertsError = error;
        return;
      }
      this.savingAlerts = true;
      try {
        await this.apiUpdateAppSettings({
          propertyPageAlerts: normalized,
        });
        this.propertyPageAlerts = normalized;
        this.propertyPageAlertsDirty = false;
        this.alertsSuccess = `Saved ${normalized.length} property page alert(s).`;
        this.$store.commit("setAlert", {
          type: "success",
          text: this.alertsSuccess,
        });
      } catch (err) {
        this.alertsError =
          err?.message || "Failed to save property page alerts.";
        this.$store.commit("setAlert", {
          type: "error",
          text: this.alertsError,
        });
      } finally {
        this.savingAlerts = false;
      }
    },
    async loadSpecialCodes() {
      this.specialCodesError = null;
      try {
        const doc = await firebase
          .firestore()
          .collection(SPECIAL_CODES_COLLECTION)
          .doc(SPECIAL_CODES_DOC_ID)
          .get();
        const list = doc.exists && Array.isArray(doc.data()?.codes)
          ? doc.data().codes
          : [];
        this.specialCodes = list.map(normalizeSpecialCode);
      } catch (err) {
        this.specialCodesError =
          err?.message || "Failed to load special codes.";
        this.specialCodes = [];
      }
    },
    openSpecialCodeDialog(index = null, usePro10Template = false) {
      this.specialCodeDialogError = null;
      this.specialCodesError = null;
      this.specialCodesSuccess = null;
      if (index === null || index === undefined) {
        this.specialCodeEditIndex = null;
        this.specialCodeForm = usePro10Template
          ? defaultPro10SpecialCode()
          : emptySpecialCode();
      } else {
        this.specialCodeEditIndex = index;
        this.specialCodeForm = {
          ...normalizeSpecialCode(this.specialCodes[index]),
        };
      }
      this.specialCodeDialog = true;
    },
    closeSpecialCodeDialog() {
      this.specialCodeDialog = false;
      this.specialCodeDialogError = null;
      this.specialCodeEditIndex = null;
      this.specialCodeForm = emptySpecialCode();
    },
    async applySpecialCodeDialog() {
      const next = normalizeSpecialCode(this.specialCodeForm);
      const draft = [...this.specialCodes];
      if (this.specialCodeEditIndex === null) {
        draft.push(next);
      } else {
        draft.splice(this.specialCodeEditIndex, 1, next);
      }
      const error = validateSpecialCodes(draft);
      if (error) {
        this.specialCodeDialogError = error;
        return;
      }
      this.specialCodes = draft;
      this.closeSpecialCodeDialog();
      await this.saveSpecialCodes();
    },
    async removeSpecialCode(index) {
      this.specialCodes = this.specialCodes.filter((_, i) => i !== index);
      this.specialCodesSuccess = null;
      await this.saveSpecialCodes();
    },
    async saveSpecialCodes() {
      this.specialCodesError = null;
      this.specialCodesSuccess = null;
      const normalized = this.specialCodes.map(normalizeSpecialCode);
      const error = validateSpecialCodes(normalized);
      if (error) {
        this.specialCodesError = error;
        return;
      }
      this.savingSpecialCodes = true;
      try {
        await firebase
          .firestore()
          .collection(SPECIAL_CODES_COLLECTION)
          .doc(SPECIAL_CODES_DOC_ID)
          .set(
            {
              codes: normalized,
              updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            },
            { merge: true },
          );
        this.specialCodes = normalized;
        this.specialCodesSuccess = `Saved ${normalized.length} special code(s).`;
        this.$store.commit("setAlert", {
          type: "success",
          text: this.specialCodesSuccess,
        });
      } catch (err) {
        this.specialCodesError =
          err?.message || "Failed to save special codes.";
        this.$store.commit("setAlert", {
          type: "error",
          text: this.specialCodesError,
        });
      } finally {
        this.savingSpecialCodes = false;
      }
    },
    getAppSettings() {
      this.appSettings = this.$store.getters.appSettings;
    },
    async saveGlobalAppSettings() {
      const payload = { ...this.editSettings };
      // Template mappings are owned by /templates — do not overwrite on save.
      delete payload.propertyTypeTemplates;
      delete payload.propertyTypeTemplatesText;

      const normalizedAlerts = this.propertyPageAlerts.map(
        normalizePropertyPageAlert,
      );
      const alertError = validatePropertyPageAlerts(normalizedAlerts);
      if (alertError) {
        this.alertsError = alertError;
        this.$store.commit("setAlert", {
          type: "error",
          text: `Cannot save settings: ${alertError}`,
        });
        return;
      }
      payload.propertyPageAlerts = normalizedAlerts;

      const normalizedVideos = this.howToVideos.map(normalizeHowToVideo);
      const videoError = validateHowToVideos(normalizedVideos);
      if (videoError) {
        this.howToVideosError = videoError;
        this.$store.commit("setAlert", {
          type: "error",
          text: `Cannot save settings: ${videoError}`,
        });
        return;
      }
      payload.howToVideos = normalizedVideos;
      Object.assign(payload, trainingUrlFieldsFromHowToVideos(normalizedVideos));

      try {
        await this.apiUpdateAppSettings(payload);
        this.propertyPageAlertsDirty = false;
        this.howToVideosDirty = false;
        this.$router.back();
      } catch (error) {
        this.$store.commit("setAlert", {
          type: "error",
          text: `Failed to save settings: ${error.message}`,
        });
      }
    },
    async undeleteProperty() {
      const propertyId = this.formData.propertyId;
      console.log("undeleteProperty", propertyId);
      await this.apiUndeleteProperty(propertyId);
      this.deleteMessage = `Property ${propertyId} undeleted`;
    },
  },
};
</script>

<style>
.switch--large {
  transform: scale(1.5);
  margin-left: 10px;
}
.switch--large .v-input--selection-controls__ripple {
  float: left;
  width: 48px;
  height: 28px;
}
</style>
